/**
 * packages/ui-shell/src/components/ExportModal.ts
 * アニメーション録画・エクスポート＆SNSシェア（𝕏投稿）対応モーダル
 */
import { UiState } from '../state/UiState';
import { t } from '../i18n/i18n';

export interface ExportResultData {
  blob: Blob;
  fileName: string;
  mimeType: string;
}

export interface ExportModalCallbacks {
  onStartExport?: (
    format: 'gif' | 'mp4' | 'webm',
    durationSec: number,
    onProgress: (stage: 'capturing' | 'encoding', percent: number) => void
  ) => Promise<ExportResultData | void>;
  onTriggerPoke?: () => void;
  onUpgradeRequested?: (feature: string) => void;
}

export class ExportModal {
  private element: HTMLElement;
  private uiState: UiState;
  private callbacks: ExportModalCallbacks;

  private selectedFormat: 'gif' | 'mp4' | 'webm' = 'mp4';
  private selectedDuration = 3;
  private isRecording = false;
  private currentStep: 'config' | 'complete' = 'config';
  private lastResult: ExportResultData | null = null;
  private lastObjectUrl: string | null = null;

  // Step 1: Config Elements
  private configViewEl!: HTMLElement;
  private titleEl!: HTMLElement;
  private fmtTitleEl!: HTMLElement;
  private fmtGifBtn!: HTMLButtonElement;
  private fmtMp4Btn!: HTMLButtonElement;
  private fmtWebmBtn!: HTMLButtonElement;
  private secTitleEl!: HTMLElement;
  private watermarkBannerEl!: HTMLElement;
  private progressBoxEl!: HTMLElement;
  private countdownEl!: HTMLElement;
  private progressLabelEl!: HTMLElement;
  private progressBarEl!: HTMLElement;
  private btnStartRecordEl!: HTMLButtonElement;
  private btnCancelEl!: HTMLButtonElement;

  // Step 2: Complete & Share Elements
  private completeViewEl!: HTMLElement;
  private completeTitleEl!: HTMLElement;
  private completeFilenameEl!: HTMLElement;
  private previewContainerEl!: HTMLElement;
  private tweetBodyEl!: HTMLElement;
  private tweetTagsEl!: HTMLElement;
  private btnShareXEl!: HTMLButtonElement;
  private btnCopyTextEl!: HTMLButtonElement;
  private btnDownloadAgainEl!: HTMLButtonElement;
  private tipXShareEl!: HTMLElement;
  private proUpsellBannerEl!: HTMLElement;
  private copyToastEl!: HTMLElement;
  private btnNewExportEl!: HTMLButtonElement;
  private btnCloseCompleteEl!: HTMLButtonElement;
  private recordingPillEl!: HTMLElement;

  constructor(container: HTMLElement, uiState: UiState, callbacks: ExportModalCallbacks = {}) {
    this.uiState = uiState;
    this.callbacks = callbacks;

    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.element.id = 'modalExport';
    container.appendChild(this.element);

    this.render();
    this.initElements();
    this.createRecordingPill();
    this.bindEvents();

    this.uiState.subscribe(() => {
      this.updateTexts();
    });
  }

  getElement(): HTMLElement {
    return this.element;
  }

  private render(): void {
    const texts = t().export;
    const shareTexts = t().share;
    const isFree = this.uiState.isWatermarkForced();

    this.element.innerHTML = `
      <div class="modal-card" style="max-width: 520px;">
        <div class="modal-header">
          <div class="modal-title">
            <span id="exportHeaderIcon">🎥</span>
            <span id="exportModalTitle">${texts.title}</span>
          </div>
          <button class="modal-close" id="btnCloseExportModal">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Step 1: 録画・エクスポート設定 -->
          <div id="exportConfigView">
            <div class="control-group">
              <label id="exportFmtTitle" style="font-weight:600; display:block; margin-bottom:8px; font-size:13px;">${texts.format}</label>
              <div style="display:flex; gap:8px;">
                <button type="button" class="btn btn-secondary export-fmt active" id="btnExportMp4" data-fmt="mp4" style="flex:1;">${texts.fmtMp4}</button>
                <button type="button" class="btn btn-secondary export-fmt" id="btnExportGif" data-fmt="gif" style="flex:1;">${texts.fmtGif}</button>
                <button type="button" class="btn btn-secondary export-fmt" id="btnExportWebm" data-fmt="webm" style="flex:1;">${texts.fmtWebm}</button>
              </div>
            </div>

            <div class="control-group">
              <label id="exportSecTitle" style="font-weight:600; display:block; margin-bottom:8px; font-size:13px;">${texts.duration}</label>
              <div style="display:flex; gap:8px;">
                <button type="button" class="btn btn-secondary export-sec" data-sec="2" style="flex:1;">2s</button>
                <button type="button" class="btn btn-secondary export-sec active" data-sec="3" style="flex:1;">3s</button>
                <button type="button" class="btn btn-secondary export-sec" data-sec="5" style="flex:1;">5s</button>
              </div>
            </div>

            <!-- 透かしバナー案内 -->
            <div id="exportWatermarkBanner" style="margin-bottom: 14px;"></div>

            <!-- 録画ステータス＆プログレス -->
            <div id="exportProgressBox" style="display:none; background:#faf8f5; border:1px solid var(--color-card-border); border-radius:var(--radius-sm); padding:16px; text-align:center; margin-top:12px;">
              <div id="exportCountdown" style="font-size:2rem; font-weight:800; color:var(--color-primary); margin-bottom:4px;">3</div>
              <div id="exportProgressLabel" style="font-size:12px; color:var(--color-text-muted); font-weight:600;">${texts.recording}</div>
              <div style="background:#e8e0d4; height:8px; border-radius:var(--radius-pill); overflow:hidden; margin-top:10px;">
                <div id="exportProgressBar" style="width:0%; height:100%; background:var(--color-primary); transition:width 0.2s;"></div>
              </div>
            </div>

            <div class="modal-footer" style="padding: 14px 0 0; margin-top: 14px; border-top: 1px solid var(--color-card-border);">
              <button type="button" class="btn btn-secondary" id="btnCancelExport">${texts.cancel}</button>
              <button type="button" class="btn btn-primary" id="btnStartRecord">${texts.start}</button>
            </div>
          </div>

          <!-- Step 2: 書き出し完了 & SNSシェア画面 -->
          <div id="exportCompleteView" style="display:none; text-align:center;">
            <p id="exportCompleteSaved" style="font-size: 13px; color: #059669; font-weight: 600; margin-bottom: 8px;">
              ${texts.completeSaved || 'ダウンロードしました！できたアニメーションを 𝕏 でシェアしよう！'}
            </p>

            <!-- プレビュー枠 -->
            <div id="exportPreviewContainer" style="margin-bottom: 8px;"></div>

            <!-- 𝕏 ツイートプレビュー風カード -->
            <div class="tweet-preview-card" style="margin: 8px 0; padding: 10px 14px; text-align: left;">
              <div class="tweet-preview-header" style="margin-bottom: 4px;">
                <div class="tweet-preview-avatar">🍮</div>
                <div>
                  <div class="tweet-preview-author">${t().app.title}</div>
                  <div class="tweet-preview-handle">@KILROYSANWHERE</div>
                </div>
              </div>
              <div class="tweet-preview-body" id="exportTweetBody" style="font-size: 12px; line-height: 1.4;">
                ${shareTexts.tweetText}
              </div>
              <div style="font-size: 12px; color: #1d9bf0; margin-top: 2px; word-break: break-all;">
                https://jigglytouch.com
              </div>
              <div class="tweet-preview-tags" id="exportTweetTags" style="margin-top: 4px;">
                #${shareTexts.hashtags.split(',').join(' #')}
              </div>
            </div>

            <p id="exportTipXShare" style="font-size: 11px; color: var(--color-text-muted); margin-bottom: 8px;">
              ${texts.tipXShare || '💡 ダウンロードされた動画/GIFを𝕏の投稿に添付してシェアしよう！'}
            </p>

            <!-- アクションボタン群 -->
            <div style="display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
              <button type="button" class="btn btn-x-share" id="btnShareToX" style="flex: 1.2; min-width: 160px; padding: 10px 18px; font-size: 13px;">
                <span>𝕏</span> <span id="btnShareToXText">${texts.shareToX || '𝕏 (Twitter) でシェア'}</span>
              </button>
              <button type="button" class="btn btn-secondary" id="btnCopyShareText" style="flex: 1; min-width: 120px; padding: 10px 14px; font-size: 12px;">
                ${texts.copyShareText || '📋 投稿文をコピー'}
              </button>
              <button type="button" class="btn btn-secondary" id="btnDownloadAgain" style="padding: 10px 14px; font-size: 12px;" title="${texts.downloadAgain || '再ダウンロード'}">
                ${texts.downloadAgain || '📥 再保存'}
              </button>
            </div>

            <div style="margin-top: 6px;">
              <div class="share-copy-toast" id="exportCopyToast">
                ✓ ${texts.copiedText || '投稿文をクリップボードにコピーしました！'}
              </div>
            </div>

            <!-- 無料版向け Pro版訴求バナー -->
            <div id="exportProUpsellBanner"></div>

            <div class="modal-footer" style="padding: 14px 0 0; margin-top: 14px; border-top: 1px solid var(--color-card-border); display: flex; justify-content: space-between;">
              <button type="button" class="btn btn-secondary" id="btnNewExport">${texts.newExport || 'もう一度書き出す'}</button>
              <button type="button" class="btn btn-primary" id="btnCloseComplete">${texts.close || '閉じる'}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private initElements(): void {
    // Step 1
    this.configViewEl = this.element.querySelector('#exportConfigView') as HTMLElement;
    this.titleEl = this.element.querySelector('#exportModalTitle') as HTMLElement;
    this.fmtTitleEl = this.element.querySelector('#exportFmtTitle') as HTMLElement;
    this.fmtGifBtn = this.element.querySelector('#btnExportGif') as HTMLButtonElement;
    this.fmtMp4Btn = this.element.querySelector('#btnExportMp4') as HTMLButtonElement;
    this.fmtWebmBtn = this.element.querySelector('#btnExportWebm') as HTMLButtonElement;
    this.secTitleEl = this.element.querySelector('#exportSecTitle') as HTMLElement;
    this.watermarkBannerEl = this.element.querySelector('#exportWatermarkBanner') as HTMLElement;
    this.progressBoxEl = this.element.querySelector('#exportProgressBox') as HTMLElement;
    this.countdownEl = this.element.querySelector('#exportCountdown') as HTMLElement;
    this.progressLabelEl = this.element.querySelector('#exportProgressLabel') as HTMLElement;
    this.progressBarEl = this.element.querySelector('#exportProgressBar') as HTMLElement;
    this.btnStartRecordEl = this.element.querySelector('#btnStartRecord') as HTMLButtonElement;
    this.btnCancelEl = this.element.querySelector('#btnCancelExport') as HTMLButtonElement;

    // Step 2
    this.completeViewEl = this.element.querySelector('#exportCompleteView') as HTMLElement;
    this.completeSavedEl = this.element.querySelector('#exportCompleteSaved') as HTMLElement;
    this.previewContainerEl = this.element.querySelector('#exportPreviewContainer') as HTMLElement;
    this.tweetBodyEl = this.element.querySelector('#exportTweetBody') as HTMLElement;
    this.tweetTagsEl = this.element.querySelector('#exportTweetTags') as HTMLElement;
    this.btnShareXEl = this.element.querySelector('#btnShareToX') as HTMLButtonElement;
    this.btnCopyTextEl = this.element.querySelector('#btnCopyShareText') as HTMLButtonElement;
    this.btnDownloadAgainEl = this.element.querySelector('#btnDownloadAgain') as HTMLButtonElement;
    this.tipXShareEl = this.element.querySelector('#exportTipXShare') as HTMLElement;
    this.proUpsellBannerEl = this.element.querySelector('#exportProUpsellBanner') as HTMLElement;
    this.copyToastEl = this.element.querySelector('#exportCopyToast') as HTMLElement;
    this.btnNewExportEl = this.element.querySelector('#btnNewExport') as HTMLButtonElement;
    this.btnCloseCompleteEl = this.element.querySelector('#btnCloseComplete') as HTMLButtonElement;
  }
  private completeSavedEl!: HTMLElement;

  private bindEvents(): void {
    this.element.querySelector('#btnCloseExportModal')?.addEventListener('click', () => this.hide());
    this.btnCancelEl?.addEventListener('click', () => this.hide());
    this.btnCloseCompleteEl?.addEventListener('click', () => this.hide());

    this.element.addEventListener('click', (e) => {
      if (e.target === this.element && !this.isRecording) {
        this.hide();
      }
    });

    // フォーマット選択
    this.element.querySelectorAll<HTMLButtonElement>('.export-fmt').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.element.querySelectorAll('.export-fmt').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedFormat = btn.getAttribute('data-fmt') as any;
      });
    });

    // 秒数選択
    this.element.querySelectorAll<HTMLButtonElement>('.export-sec').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.element.querySelectorAll('.export-sec').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        this.selectedDuration = parseInt(btn.getAttribute('data-sec') || '3', 10);
      });
    });

    // 録画スタート
    this.btnStartRecordEl.addEventListener('click', () => {
      this.startRecord();
    });

    // 𝕏 でシェア
    this.btnShareXEl.addEventListener('click', () => {
      this.shareToX();
    });

    // テキストコピー
    this.btnCopyTextEl.addEventListener('click', () => {
      this.copyShareText();
    });

    // 再ダウンロード
    this.btnDownloadAgainEl.addEventListener('click', () => {
      this.reDownload();
    });

    // もう一度書き出す
    this.btnNewExportEl.addEventListener('click', () => {
      this.resetToConfig();
    });
  }

  show(): void {
    this.resetToConfig();
    this.updateTexts();
    this.element.classList.add('show');
  }

  hide(): void {
    if (this.isRecording) return;
    this.element.classList.remove('show');
    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
      this.lastObjectUrl = null;
    }
  }

  private resetToConfig(): void {
    this.currentStep = 'config';
    this.configViewEl.style.display = 'block';
    this.completeViewEl.style.display = 'none';
    this.progressBoxEl.style.display = 'none';
    this.progressBarEl.style.width = '0%';
    this.btnStartRecordEl.disabled = false;
    this.countdownEl.textContent = '3';
    this.hideRecordingPill();
    const headerIcon = this.element.querySelector('#exportHeaderIcon');
    if (headerIcon) headerIcon.textContent = '🎥';
    if (this.titleEl) this.titleEl.textContent = t().export.title;
  }

  private createRecordingPill(): void {
    if (typeof document === 'undefined') return;
    this.recordingPillEl = document.createElement('div');
    this.recordingPillEl.className = 'export-recording-pill';
    this.recordingPillEl.id = 'exportRecordingPill';
    this.recordingPillEl.innerHTML = `
      <div class="rec-badge" style="display:flex; align-items:center; gap:6px; color:#ef4444; font-weight:800; font-size:12px;">
        <span class="rec-pulse-dot"></span>
        <span id="recStatusText">REC</span>
        <span id="recTimerText" style="color:#ffffff; font-weight:700; font-family:monospace; font-size:13px; margin-left:2px;">3.0s</span>
      </div>
      <div style="width:1px; height:14px; background:rgba(255,255,255,0.25);"></div>
      <div id="recHintText" style="font-size:12px; font-weight:500; color:rgba(255,255,255,0.9); display:flex; align-items:center; gap:4px;">
        👆 画面をタッチして揺らしてください！
      </div>
    `;
    document.body.appendChild(this.recordingPillEl);
  }

  private showRecordingPill(durationSec: number): void {
    if (!this.recordingPillEl) return;
    const texts = t().export;
    const hintEl = this.recordingPillEl.querySelector('#recHintText');
    const statusEl = this.recordingPillEl.querySelector('#recStatusText');
    const timerEl = this.recordingPillEl.querySelector('#recTimerText');
    if (hintEl) hintEl.textContent = `👆 ${texts.touchHint || '画面をタッチして揺らしてください！'}`;
    if (statusEl) statusEl.textContent = texts.recTimer || 'REC';
    if (timerEl) timerEl.textContent = `${durationSec.toFixed(1)}s`;
    this.recordingPillEl.classList.add('show');
  }

  private updateRecordingPillTime(remainingSec: number): void {
    if (!this.recordingPillEl) return;
    const timerEl = this.recordingPillEl.querySelector('#recTimerText');
    if (timerEl) timerEl.textContent = `${Math.max(0, remainingSec).toFixed(1)}s`;
  }

  private hideRecordingPill(): void {
    if (!this.recordingPillEl) return;
    this.recordingPillEl.classList.remove('show');
  }

  private updateTexts(): void {
    const texts = t().export;
    const shareTexts = t().share;
    const isFree = this.uiState.isWatermarkForced();

    if (this.currentStep === 'config') {
      if (this.titleEl) this.titleEl.textContent = texts.title;
      const headerIcon = this.element.querySelector('#exportHeaderIcon');
      if (headerIcon) headerIcon.textContent = '🎥';
    } else {
      if (this.titleEl) this.titleEl.textContent = texts.completeTitle || shareTexts.exportSuccessTitle || '書き出し完了！🎉';
      const headerIcon = this.element.querySelector('#exportHeaderIcon');
      if (headerIcon) headerIcon.textContent = '🎉';
    }

    if (this.fmtTitleEl) this.fmtTitleEl.textContent = texts.format;
    if (this.fmtGifBtn) this.fmtGifBtn.textContent = texts.fmtGif;
    if (this.fmtMp4Btn) this.fmtMp4Btn.textContent = texts.fmtMp4;
    if (this.fmtWebmBtn) this.fmtWebmBtn.textContent = texts.fmtWebm;
    if (this.secTitleEl) this.secTitleEl.textContent = texts.duration;
    if (this.btnCancelEl) this.btnCancelEl.textContent = texts.cancel;
    if (this.btnStartRecordEl && !this.isRecording) this.btnStartRecordEl.textContent = texts.start;

    // ウォーターマーク案内バナーの更新
    if (this.watermarkBannerEl) {
      if (isFree) {
        this.watermarkBannerEl.innerHTML = `
          <div style="background:#fff8f0; border:1px solid #fed7aa; border-radius:8px; padding:10px 14px; font-size:12px; line-height:1.5; color:#7c2d12; display:flex; align-items:center; justify-content:space-between; gap:10px;">
            <div>
              <span>🍮 <strong>${texts.freeNotice || '※ Web無料版では右下に可愛い透かしロゴ「🍮 Jiggly Touch」が入ります。SNSシェア大歓迎！'}</strong></span>
            </div>
            <button type="button" class="btn btn-pro" id="btnConfigUpgradePrompt" style="padding:4px 10px; font-size:11px; white-space:nowrap; flex-shrink:0;">
              ${texts.viewProBtn || '👑 Pro版を見る'}
            </button>
          </div>
        `;
        this.watermarkBannerEl.querySelector('#btnConfigUpgradePrompt')?.addEventListener('click', () => {
          this.callbacks.onUpgradeRequested?.(t().upgrade.perkExport);
        });
      } else {
        this.watermarkBannerEl.innerHTML = `
          <div style="background:#ecfdf5; border:1px solid #a7f3d0; border-radius:8px; padding:8px 12px; font-size:12px; color:#065f46; display:flex; align-items:center; gap:6px;">
            <span>${texts.proNotice || '👑 Pro版有効：透かしロゴなし（完全除去）で高画質保存されます。'}</span>
          </div>
        `;
      }
    }

    // Step 2 の文言更新
    if (this.completeSavedEl) {
      this.completeSavedEl.textContent = texts.completeSaved || 'ダウンロードしました！できたアニメーションを 𝕏 でシェアしよう！';
    }
    if (this.tweetBodyEl) {
      this.tweetBodyEl.textContent = shareTexts.tweetText;
    }
    if (this.tweetTagsEl) {
      this.tweetTagsEl.textContent = `#${shareTexts.hashtags.split(',').join(' #')}`;
    }
    if (this.btnShareXEl) {
      const textSpan = this.btnShareXEl.querySelector('#btnShareToXText');
      if (textSpan) textSpan.textContent = texts.shareToX || '𝕏 (Twitter) でシェア';
    }
    if (this.btnCopyTextEl) {
      this.btnCopyTextEl.textContent = texts.copyShareText || '📋 投稿文をコピー';
    }
    if (this.btnDownloadAgainEl) {
      this.btnDownloadAgainEl.textContent = texts.downloadAgain || '📥 再保存';
    }
    if (this.tipXShareEl) {
      this.tipXShareEl.textContent = texts.tipXShare || '💡 ダウンロードされた動画/GIFを𝕏の投稿に添付してシェアしよう！';
    }
    if (this.copyToastEl) {
      this.copyToastEl.textContent = `✓ ${texts.copiedText || '投稿文をクリップボードにコピーしました！'}`;
    }
    if (this.btnNewExportEl) {
      this.btnNewExportEl.textContent = texts.newExport || 'もう一度書き出す';
    }
    if (this.btnCloseCompleteEl) {
      this.btnCloseCompleteEl.textContent = texts.close || '閉じる';
    }

    // 無料版向け Pro版アップセルバナー
    if (this.proUpsellBannerEl) {
      if (isFree) {
        this.proUpsellBannerEl.innerHTML = `
          <div style="background: linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%); border: 1px solid #fecdd3; border-radius: 10px; padding: 10px 14px; margin-top: 14px; text-align: left; display: flex; align-items: center; justify-content: space-between; gap: 10px;">
            <div>
              <div style="font-weight: 700; font-size: 12px; color: #9f1239;">${texts.proUpsellTitle || '✨ 透かしロゴを消したいですか？'}</div>
              <div style="font-size: 11px; color: #be123c;">${texts.proUpsellDesc || 'Pro版なら透かしなし完全保存・自動揺れ・可愛い触覚効果音 ($1.99)'}</div>
            </div>
            <button type="button" class="btn btn-pro" id="btnCompleteProUpgrade" style="padding: 6px 12px; font-size: 11px; white-space: nowrap; flex-shrink: 0;">
              ${texts.viewProBtn || '👑 Pro版を見る'}
            </button>
          </div>
        `;
        this.proUpsellBannerEl.querySelector('#btnCompleteProUpgrade')?.addEventListener('click', () => {
          this.callbacks.onUpgradeRequested?.(t().upgrade.perkExport);
        });
      } else {
        this.proUpsellBannerEl.innerHTML = '';
      }
    }
  }

  private async startRecord(): Promise<void> {
    if (!this.callbacks.onStartExport || this.isRecording) return;
    const texts = t().export;

    this.isRecording = true;
    this.btnStartRecordEl.disabled = true;
    this.progressBoxEl.style.display = 'block';
    this.progressBarEl.style.width = '0%';

    // カウントダウン (3 -> 2 -> 1)
    for (let c = 3; c > 0; c--) {
      this.countdownEl.textContent = c.toString();
      this.progressLabelEl.textContent = texts.recording;
      await new Promise((r) => setTimeout(r, 1000));
    }

    // カウントダウン完了：モーダルを非表示にして画像タッチを開放！
    this.element.style.pointerEvents = 'none';
    this.element.classList.remove('show');
    this.showRecordingPill(this.selectedDuration);

    const totalDuration = this.selectedDuration;
    const startTime = Date.now();

    try {
      const result = await this.callbacks.onStartExport(
        this.selectedFormat,
        this.selectedDuration,
        (stage, percent) => {
          if (stage === 'capturing') {
            const elapsedSec = (Date.now() - startTime) / 1000;
            const remainingSec = Math.max(0, totalDuration - elapsedSec);
            this.updateRecordingPillTime(remainingSec);
          } else {
            // エンコード中：フローティングを隠し、モーダルを復帰して進捗バーを表示
            this.hideRecordingPill();
            this.element.style.pointerEvents = '';
            this.element.classList.add('show');
            this.progressBoxEl.style.display = 'block';
            this.countdownEl.textContent = '⚙️';
            this.progressLabelEl.textContent = texts.processing;
            this.progressBarEl.style.width = `${70 + Math.round(percent * 0.3)}%`;
          }
        }
      );

      this.isRecording = false;
      this.btnStartRecordEl.disabled = false;
      this.progressBoxEl.style.display = 'none';
      this.hideRecordingPill();
      this.element.style.pointerEvents = '';

      if (result) {
        this.showCompleteView(result);
      } else {
        this.hide();
      }
    } catch (err) {
      console.error('[ExportModal] Export failed:', err);
      this.isRecording = false;
      this.btnStartRecordEl.disabled = false;
      this.progressBoxEl.style.display = 'none';
      this.hideRecordingPill();
      this.element.style.pointerEvents = '';
      this.element.classList.add('show');
      alert(`エクスポートに失敗しました: ${(err as Error).message}`);
    }
  }

  private showCompleteView(result: ExportResultData): void {
    this.lastResult = result;
    this.currentStep = 'complete';
    this.configViewEl.style.display = 'none';
    this.completeViewEl.style.display = 'block';

    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
    }
    this.lastObjectUrl = URL.createObjectURL(result.blob);

    if (this.previewContainerEl) {
      if (result.mimeType.startsWith('image/')) {
        this.previewContainerEl.innerHTML = `
          <img src="${this.lastObjectUrl}" alt="Preview" style="max-height: 110px; max-width: 100%; border-radius: var(--radius-md); box-shadow: var(--shadow-card); display: block; margin: 0 auto;" />
        `;
      } else {
        this.previewContainerEl.innerHTML = `
          <video src="${this.lastObjectUrl}" autoplay loop muted playsinline style="max-height: 110px; max-width: 100%; border-radius: var(--radius-md); box-shadow: var(--shadow-card); display: block; margin: 0 auto;"></video>
        `;
      }
    }

    this.updateTexts();
    this.element.style.pointerEvents = '';
    this.element.classList.add('show');
  }

  private async shareToX(): Promise<void> {
    const texts = t().share;
    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const url = 'https://jigglytouch.com';
    const body = texts.tweetText;
    const hashtags = texts.hashtags;
    const fullText = `${body}\n${url}\n#${hashtags.split(',').join(' #')}`;

    // 1. スマホ (iOS / Android) かつ Web Share API でファイル共有可能な場合のみOS共有シートを起動
    if (isMobile && this.lastResult && typeof navigator !== 'undefined' && 'canShare' in navigator) {
      try {
        const file = new File([this.lastResult.blob], this.lastResult.fileName, {
          type: this.lastResult.mimeType,
        });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'Jiggly Touch',
            text: fullText,
            files: [file],
          });
          return;
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
      }
    }

    // 2. PC (Windows/Mac) または Web Share API 未対応の場合:
    // 自動でクリップボードに投稿文をコピーし、トーストを表示
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = fullText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      if (this.copyToastEl) {
        const exportTexts = t().export;
        this.copyToastEl.textContent = `✓ ${exportTexts.copiedText || '投稿文をコピーしました！'} 𝕏を開きます...`;
        this.copyToastEl.classList.add('show');
        setTimeout(() => this.copyToastEl?.classList.remove('show'), 3500);
      }
    } catch (_) {}

    // 直接 𝕏 (Twitter) の投稿画面を新しいタブで開く
    const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(body)}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent(hashtags)}`;
    if (typeof window !== 'undefined') {
      window.open(intentUrl, '_blank', 'noopener,noreferrer');
    }
  }

  private async copyShareText(): Promise<void> {
    const texts = t().share;
    const url = 'https://jigglytouch.com';
    const fullText = `${texts.tweetText}\n${url}\n#${texts.hashtags.split(',').join(' #')}`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = fullText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      if (this.copyToastEl) {
        this.copyToastEl.classList.add('show');
        setTimeout(() => {
          this.copyToastEl.classList.remove('show');
        }, 2500);
      }
    } catch {}
  }

  private reDownload(): void {
    if (!this.lastResult) return;
    const url = URL.createObjectURL(this.lastResult.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.lastResult.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
